import { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useProvider, useUpdateProvider, useProviders } from '../../hooks/useProviders';
import { useTemplates } from '../../hooks/useTemplates';
import type { UpdateProviderRequest } from '../../types';
import { Loader2 } from 'lucide-react';

export const ProviderEditForm = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const { data: provider, isLoading: isLoadingProvider } = useProvider(id!);
  const updateProvider = useUpdateProvider();
  const { data: templates } = useTemplates();
  const { data: providers } = useProviders();
  const [isCustomProvider, setIsCustomProvider] = useState(false);

  const { register, handleSubmit, formState: { errors }, setValue, setError, clearErrors } = useForm<UpdateProviderRequest>();

  const selectedTemplate = templates?.find(t => t.id === provider?.templateId);

  const normalizeScopes = (scopes?: string | string[], fallback?: string | string[]) => {
    const value = scopes ?? fallback ?? '';
    const scopeString = Array.isArray(value) ? value.join(' ') : value;
    return scopeString
      .split(/\s+/)
      .filter(Boolean)
      .join(' ')
      .trim();
  };

  const validateScopes = (value?: string | string[]) => {
    const normalized = normalizeScopes(value, selectedTemplate?.defaultScopes);
    if (isCustomProvider && !normalized) {
      return 'Scopes are required for custom providers';
    }
    return true;
  };

  // Load provider data
  useEffect(() => {
    if (provider) {
      setIsCustomProvider(provider.isCustomProvider);
      setValue('name', provider.name);
      setValue('clientId', provider.clientId);

      // Always load scopes (for both custom and template-based providers)
      const scopesValue = typeof provider.scopes === 'string'
        ? provider.scopes
        : provider.scopes?.join(' ') || '';
      setValue('scopes', scopesValue);

      if (provider.isCustomProvider) {
        setValue('authorizationUrl', provider.authorizationUrl || '');
        setValue('tokenUrl', provider.tokenUrl || '');
        setValue('userInfoUrl', provider.userInfoUrl || '');
        setValue('revocationUrl', provider.revocationUrl || '');
        setValue('tokenEndpointAuthMethod', provider.tokenEndpointAuthMethod || 'client_secret_post');
      }

      // Load RequiresPkce for all providers (custom and template-based)
      setValue('requiresPkce', provider.requiresPkce || false);
      setValue('isActive', provider.isActive);
    }
  }, [provider, setValue]);

  const onSubmit = async (data: UpdateProviderRequest) => {
    if (!id) return;

    try {
      const targetScopes = normalizeScopes(
        data.scopes ?? data.scopesOverride,
        selectedTemplate?.defaultScopes
      );
      const targetClientId = (data.clientId ?? '').trim();

      const duplicate = providers?.some(p =>
        p.id !== id &&
        (p.clientId ?? '').trim() === targetClientId &&
        normalizeScopes(p.scopes) === targetScopes
      );

      if (duplicate) {
        setError('clientId', { type: 'manual', message: 'A provider with the same client ID and scopes already exists.' });
        setError('scopes', { type: 'manual', message: 'Duplicate client ID and scopes combination.' });
        return;
      }

      clearErrors(['clientId', 'scopes']);

      const payload: UpdateProviderRequest = {
        templateId: provider?.templateId ?? null,
        name: data.name,
        clientId: data.clientId,
        clientSecret: data.clientSecret || undefined,
        isActive: data.isActive,
      };

      if (isCustomProvider) {
        // For custom providers, always include all fields
        payload.authorizationUrl = data.authorizationUrl;
        payload.tokenUrl = data.tokenUrl;
        payload.userInfoUrl = data.userInfoUrl;
        payload.revocationUrl = data.revocationUrl;
        payload.scopes = data.scopes;
        payload.requiresPkce = data.requiresPkce;
        payload.tokenEndpointAuthMethod = data.tokenEndpointAuthMethod;
      } else {
        // For template-based providers
        payload.requiresPkce = data.requiresPkce;

        // Send scopes as scopesOverride for template-based providers
        if (data.scopes) {
          payload.scopesOverride = data.scopes;
        }
      }

      await updateProvider.mutateAsync({
        providerId: id,
        data: payload,
      });

      navigate('/providers');
    } catch (error) {
      console.error('Failed to update provider:', error);
    }
  };

  if (isLoadingProvider) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Provider Not Found</h3>
        <p className="text-gray-600 mb-4">The requested provider could not be found.</p>
        <Link to="/providers" className="btn btn-primary px-6 py-2">
          Back to Providers
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Edit Identity Provider</h2>
        <p className="text-gray-600 mt-1">Update provider configuration</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="card p-6 space-y-6">
        {/* Basic Fields */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Name</label>
          <input
            {...register('name', { required: true })}
            type="text"
            className="input w-full"
            placeholder="My Google Account"
          />
          {errors.name && <p className="text-sm text-red-600 mt-1">Name is required</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Client ID</label>
          <input
            {...register('clientId', { required: true })}
            type="text"
            className="input w-full"
          />
          {errors.clientId && (
            <p className="text-sm text-red-600 mt-1">
              {errors.clientId.message || 'Client ID is required'}
            </p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Client Secret <span className="text-gray-500 font-normal">(leave empty to keep current)</span>
          </label>
          <input
            {...register('clientSecret')}
            type="password"
            className="input w-full"
            placeholder="Enter new client secret..."
          />
        </div>

        {/* Scopes (always visible for editing) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Scopes</label>
          <input
            {...register('scopes', { validate: validateScopes })}
            type="text"
            className="input w-full"
            placeholder={
              selectedTemplate
                ? `Template default: ${
                    Array.isArray(selectedTemplate.defaultScopes)
                      ? selectedTemplate.defaultScopes.join(' ')
                      : selectedTemplate.defaultScopes
                  }`
                : 'openid profile email'
            }
          />
          <p className="text-sm text-gray-500 mt-1">
            Space-separated list of OAuth scopes
            {!isCustomProvider && ' (leave empty to use template default)'}
          </p>
          {errors.scopes && (
            <p className="text-sm text-red-600 mt-1">
              {errors.scopes.message || 'Scopes are required for custom providers'}
            </p>
          )}
        </div>

        {/* Custom Provider Fields (only for custom providers) */}
        {isCustomProvider && (
          <>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Authorization URL
              </label>
              <input
                {...register('authorizationUrl', { required: isCustomProvider })}
                type="url"
                className="input w-full"
                placeholder="https://oauth.example.com/authorize"
              />
              {errors.authorizationUrl && (
                <p className="text-sm text-red-600 mt-1">Authorization URL is required</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Token URL</label>
              <input
                {...register('tokenUrl', { required: isCustomProvider })}
                type="url"
                className="input w-full"
                placeholder="https://oauth.example.com/token"
              />
              {errors.tokenUrl && <p className="text-sm text-red-600 mt-1">Token URL is required</p>}
            </div>
          </>
        )}

        {/* PKCE Toggle - Available for all providers */}
        <div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('requiresPkce')}
              className="rounded"
            />
            <span className="text-sm font-medium">Require PKCE (Proof Key for Code Exchange)</span>
          </label>
          <p className="text-sm text-gray-500 mt-1">
            Enable PKCE for enhanced security. Some providers (like Autodesk) may not support this.
            {!isCustomProvider && ' Overrides template default if changed.'}
          </p>
        </div>

        {/* Active Toggle */}
        <div>
          <label className="flex items-center space-x-2 cursor-pointer">
            <input
              type="checkbox"
              {...register('isActive')}
              className="rounded"
            />
            <span className="text-sm font-medium">Active</span>
          </label>
          <p className="text-sm text-gray-500 mt-1">Inactive providers cannot be used for new connections</p>
        </div>

        {/* Actions */}
        <div className="flex space-x-4 pt-4">
          <button
            type="submit"
            disabled={updateProvider.isPending}
            className="btn btn-primary px-6 py-2 flex items-center space-x-2"
          >
            {updateProvider.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            <span>{updateProvider.isPending ? 'Updating...' : 'Update Provider'}</span>
          </button>
          <button
            type="button"
            onClick={() => navigate('/providers')}
            className="btn btn-outline px-6 py-2"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};
